import React, { useState, useEffect, useRef } from 'react';
import './Realizations.css';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExpand, FaCheckCircle } from 'react-icons/fa';

const Realizations = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [filter, setFilter] = useState('all');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Photos des réalisations
  const realizations = [
    {
      id: 1,
      image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=800',
      title: 'Rénovation Salle de Bain Complète',
      description: 'Installation complète avec douche italienne, robinetterie haut de gamme et carrelage moderne',
      category: 'renovation'
    },
    {
      id: 2,
      image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800',
      title: 'Installation Chauffe-eau Thermodynamique',
      description: 'Pose d\'un chauffe-eau thermodynamique dernière génération avec optimisation énergétique',
      category: 'installation'
    },
    {
      id: 3,
      image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800',
      title: 'Plomberie Cuisine Moderne',
      description: 'Remplacement complet de la plomberie et installation de robinetterie design',
      category: 'installation'
    },
    {
      id: 4,
      image: 'https://images.unsplash.com/photo-1604014237800-1c9102c219da?w=800',
      title: 'WC Suspendu Design',
      description: 'Installation de WC suspendu avec bâti-support et système de chasse économique',
      category: 'installation'
    },
    {
      id: 5,
      image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800',
      title: 'Détection et Réparation Fuite',
      description: 'Détection par caméra thermique et réparation de fuite cachée dans les murs',
      category: 'depannage'
    },
    {
      id: 6,
      image: 'https://images.unsplash.com/photo-1563298723-dcfebaa392e3?w=800',
      title: 'Rénovation Appartement Complet',
      description: 'Rénovation totale de la plomberie d\'un appartement 3 pièces avec mise aux normes',
      category: 'renovation'
    },
    {
      id: 7,
      image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=800',
      title: 'Douche Italienne Premium',
      description: 'Installation de douche à l\'italienne avec receveur extra-plat et paroi en verre',
      category: 'renovation'
    },
    {
      id: 8,
      image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800',
      title: 'Système de Chauffage',
      description: 'Installation et raccordement d\'un système de chauffage central moderne',
      category: 'installation'
    }
  ];

  const categories = [
    { id: 'all', label: 'Tous les Projets' },
    { id: 'renovation', label: 'Rénovations' },
    { id: 'installation', label: 'Installations' },
    { id: 'depannage', label: 'Dépannages' }
  ];

  const filteredRealizations = filter === 'all' 
    ? realizations 
    : realizations.filter(item => item.category === filter);

  const openLightbox = (index) => {
    const actualIndex = realizations.findIndex(r => r.id === filteredRealizations[index].id);
    setCurrentIndex(actualIndex);
    setSelectedImage(realizations[actualIndex]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = (currentIndex - 1 + realizations.length) % realizations.length;
    setCurrentIndex(newIndex);
    setSelectedImage(realizations[newIndex]);
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % realizations.length;
    setCurrentIndex(newIndex);
    setSelectedImage(realizations[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, currentIndex]);

  return (
    <section id="realisations" className="section realizations" ref={sectionRef}>
      <div className="container">
        <div className="section-title">
          <h2>Nos Réalisations</h2>
          <p>Découvrez quelques-uns de nos projets récents et la qualité de notre travail</p>
        </div>

        {/* Filter Buttons */}
        <div className={`filter-buttons ${isVisible ? 'reveal active' : 'reveal'}`}>
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`filter-btn ${filter === cat.id ? 'active' : ''}`}
              onClick={() => setFilter(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="realizations-grid">
          {filteredRealizations.map((item, index) => (
            <div 
              key={item.id} 
              className={`realization-card ${isVisible ? 'reveal active' : 'reveal'}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => openLightbox(index)}
            >
              <div className="realization-image-wrapper">
                <img src={item.image} alt={item.title} loading="lazy" />
                <div className="realization-overlay">
                  <div className="overlay-content">
                    <FaExpand className="expand-icon" />
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <span className="view-more">Cliquez pour agrandir</span>
                  </div>
                </div>
                <div className="realization-badge">
                  <FaCheckCircle /> Terminé
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Note */}
        <div className={`realizations-note ${isVisible ? 'reveal active' : 'reveal'}`}>
          <div className="note-icon">
            <FaCheckCircle />
          </div>
          <div className="note-content">
            <strong>Toutes nos réalisations</strong>
            <p>
              Chaque projet présenté a été réalisé avec soin et professionnalisme. 
              Tous nos travaux sont conformes aux normes en vigueur et bénéficient 
              de notre garantie décennale.
            </p>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div className="lightbox active" onClick={closeLightbox}>
          <div className="lightbox-backdrop"></div>
          
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Fermer">
            <FaTimes />
          </button>
          
          <button 
            className="lightbox-nav lightbox-prev" 
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            aria-label="Image précédente"
          >
            <FaChevronLeft />
          </button>
          
          <button 
            className="lightbox-nav lightbox-next" 
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            aria-label="Image suivante"
          >
            <FaChevronRight />
          </button>
          
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-image-wrapper">
              <img src={selectedImage.image} alt={selectedImage.title} />
            </div>
            <div className="lightbox-info">
              <div className="lightbox-counter">
                {currentIndex + 1} / {realizations.length}
              </div>
              <h3>{selectedImage.title}</h3>
              <p>{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Realizations;
